/* eslint-disable @typescript-eslint/prefer-regexp-exec */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useCallback, useMemo, useState } from 'react';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { FormApi } from 'final-form';
import { IApiResponse } from 'utils/models';
import debounce from 'utils/debounce';
import api from 'api/api';
import { mapYupObjectError, objectMap } from 'utils/helpers';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import BulksWrapper from 'components/modules/Beneficiaries/BulksModules/BulksWrapper/BulksWrapper';
import Form from 'ui/Form/Form';
import Input from 'ui/Input/Input';
import { PhoneInput, Select, SelectItem } from 'ui/Input';
import { AxiosStatus } from 'api/axiosConfig';
import noop from 'utils/noop';
import { requiredOnly } from 'utils/validation';
import Button from 'ui/Button/Button';
import Loader from 'ui/Loader/Loader';
import { StatusModal, WarningModal } from 'components/modals';

import { INetwork } from 'components/modules/Beneficiaries/Beneficiaries.model';
import { ITransformedModileBulkId } from './BulkMobileBen.model';

import styles from './EditMobileBulkBen.module.scss';

import plus from 'assets/icons/plus.svg';
import trash from 'assets/icons/trash.png';

type FormType = FormApi<IFormValues, Partial<IFormValues>>;

export interface IFormValues {
  beneficiaryData: {
    beneficiaryName: string;
    networkCode: string;
    beneficiaryWalletNumber: string;
    walletName: string;
  }[];
}

const schema = yup.object().shape({
  beneficiaryData: yup.array().of(
    yup.object().shape({
      beneficiaryName: requiredOnly('Full Name'),
      networkCode: requiredOnly('Network Code'),
      beneficiaryWalletNumber: requiredOnly('Beneficiary Wallet Number'),
      walletName: requiredOnly('Wallet Name'),
    }),
  ),
});

const validate = (values: IFormValues) => {
  try {
    schema.validateSync(values, { abortEarly: false });
  } catch (e) {
    const valErrs = mapYupObjectError<IFormValues>(e);
    return objectMap(valErrs, (v) => v[0]);
  }
  return undefined;
};

interface IEditMobileBulkBen {
  data: ITransformedModileBulkId;
  networks: INetwork[];
  onClose: () => void;
}

const EditMobileBulkBen = ({ data, networks, onClose }: IEditMobileBulkBen) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [showWarning, setShowWarning] = useState(false);
  const [submitWarningStatus, setSubmitWarningStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const warningSubmitting = submitWarningStatus === AxiosStatus.PENDING;
  const [showStatus, setShowStatus] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const onCloseStatusModal = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowStatus(false);
    setSubmitWarningStatus(AxiosStatus.DEFAULT);
    onClose();
  };

  const [isLookingUp, setLookingUp] = useState(false);
  const [lookingIndex, setLookingIndex] = useState<number | null>(null);

  const [lookingErrorText, setLookingErrorText] = useState<Record<string, string>>({});
  const updateLookingErrorText = (update: Record<string, string>) =>
    setLookingErrorText((current) => ({ ...current, ...update }));
  const removeKeyFromLookingErrorText = (keyToRemove: string) => {
    setLookingErrorText((current) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [keyToRemove]: removedKey, ...rest } = current;
      return rest;
    });
  };
  const removeBeneficiaryAtIndex = (indexToRemove: number) => {
    setLookingErrorText((current) => {
      const updatedState = { ...current };

      Object.keys(updatedState).forEach((key) => {
        const match = key.match(/^beneficiaryData\[(\d+)\]\.beneficiaryWalletNumber$/);
        if (match) {
          const currentIndex = parseInt(match[1], 10);
          if (currentIndex === indexToRemove) {
            delete updatedState[key];
          } else if (currentIndex > indexToRemove) {
            const newIndex = currentIndex - 1;
            const newKey = `beneficiaryData[${newIndex}].beneficiaryWalletNumber`;
            updatedState[newKey] = updatedState[key];
            delete updatedState[key];
          }
        }
      });

      return updatedState;
    });
  };

  let mainForm!: FormType;

  const initialValues: IFormValues = useMemo(() => {
    const beneficiaryDataArray = data.beneficiaries.map((beneficiary) => ({
      beneficiaryName: beneficiary.name,
      networkCode: beneficiary.networkCode,
      beneficiaryWalletNumber: beneficiary.phoneNumber,
      walletName: beneficiary.walletName,
    }));

    const iniitalData = {
      beneficiaryData: beneficiaryDataArray,
    };

    return iniitalData;
  }, []);

  const lookupAccount = (phoneNumber: string, network: string, index: number) => {
    api
      .get(
        `transfer/mobilemoneylookup?phoneNumber=${phoneNumber}&network=${network}&transferType=A2W`,
        {},
        true,
        true,
        undefined,
      )
      .then((response) => {
        if (response.accountName === null) {
          mainForm.mutators.changeFieldValue(`beneficiaryData[${index}].walletName`, '');
          updateLookingErrorText({ [`beneficiaryData[${index}].beneficiaryWalletNumber`]: 'Account not found' });
          setLookingUp(false);
        } else {
          mainForm.mutators.changeFieldValue(`beneficiaryData[${index}].walletName`, response.accountName);
          removeKeyFromLookingErrorText(`beneficiaryData[${index}].beneficiaryWalletNumber`);
          setLookingUp(false);
        }
      })
      .catch((error: IApiResponse) => {
        mainForm.mutators.changeFieldValue(`beneficiaryData[${index}].walletName`, '');
        if (error.description) {
          console.error(error);
          updateLookingErrorText({ [`beneficiaryData[${index}].beneficiaryWalletNumber`]: 'Account not found' });
          setLookingUp(false);
        }
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(debounce(lookupAccount, 300), []);

  const handleChangeNetworkType = (form: FormType, values: IFormValues, index: number) => ({ target }) => {
    form.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()

    if (values.beneficiaryData[index]?.beneficiaryWalletNumber) {
      setLookingUp(true);
      setLookingIndex(index);
      lookupAccount(
        values.beneficiaryData[index].beneficiaryWalletNumber,
        target.value as string,
        index,
      );
    }
  };

  const handleChangeWalletNumber = (values: IFormValues, index: number, form: FormType) => ({ target }) => {
    const previousValue = form.getFieldState(target.name as 'beneficiaryData')?.initial;
    const currentValue = form.getFieldState(target.name as 'beneficiaryData')?.value;
    if (previousValue !== currentValue && target.value) {
      setLookingUp(true);
      setLookingIndex(index);
      if (values.beneficiaryData[index]?.networkCode) {
        debounced(target.value as string, values.beneficiaryData[index]?.networkCode, index);
      }
    }
  };

  const handleDeleteBen = () => {
    setShowWarning(true);
  };

  const handleAddBen = () => {
    mainForm.mutators.push('beneficiaryData', '');
  };

  const checkFieldError = (errors, fieldName: string) => {
    if (errors[fieldName]) {
      return true;
    }
    return false;
  };

  const checkWallenNameFieldError = (errors, fieldName: string) => {
    if (errors[fieldName] || Object.prototype.hasOwnProperty.call(lookingErrorText, fieldName)) {
      return true;
    }
    return false;
  };

  const errorText = (errors, fieldName: string) => {
    if (errors[fieldName]) {
      return errors[fieldName] as string;
    }
    return '';
  };

  const errorWalletNameText = (errors, fieldName: string) => {
    if (Object.prototype.hasOwnProperty.call(lookingErrorText, fieldName)) {
      return lookingErrorText[fieldName];
    }
    if (errors[fieldName]) {
      return errors[fieldName] as string;
    }
    return '';
  };

  const onSubmit = (values: IFormValues) => {
    setSubmitStatus(AxiosStatus.PENDING);

    const getNetworkData = (code) => {
      const network = networks.find((n) => n.code === code);
      return network ? { network: network.name, networkCode: network.code, networkGipCode: network.gipCode } : null;
    };

    const mobileMoneyBeneficiaries = values.beneficiaryData.map((beneficiary) => {
      const networkData = getNetworkData(beneficiary.networkCode);

      if (networkData) {
        return {
          beneficiaryName: beneficiary.beneficiaryName,
          beneficiaryWalletNumber: beneficiary.beneficiaryWalletNumber,
          walletName: beneficiary.walletName,
          ...networkData,
        };
      }
      return null;
    });

    const postBody = {
      bulkId: data.id,
      mobileMoneyBeneficiaries,
    };

    api
      .post('/beneficiary/mobilemoney/updateBulk', postBody, false, true)
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
      })
      .catch((error: IApiResponse) => {
        console.error(error.description);
        setErrorMessage(error.description);
      })
      .finally(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        setShowStatus(true);
      });
  };

  const onSubmitWarning = () => {
    setSubmitWarningStatus(AxiosStatus.PENDING);
    api
      .post('/beneficiary/mobilemoney/deleteBulk', { bulkId: data.id })
      .then((response: IApiResponse) => {
        setSubmitWarningStatus(AxiosStatus.SUCCESS);
        setSuccessMessage(response.description);
      })
      .catch((error: IApiResponse) => {
        setSubmitWarningStatus(AxiosStatus.FAILURE);
        setErrorMessage(error.description);
      })
      .finally(() => {
        setShowWarning(false);
        setShowStatus(true);
      });
  };

  const closeWarning = () => {
    setShowWarning(false);
  };

  const loadingRow = (index: number) => isLookingUp && lookingIndex === index;

  return (
    <BulksWrapper
      onClickBack={onClose}
      onDeleteBen={handleDeleteBen}
    >
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
        mutators={{ changeFieldValue, setFieldTouched, ...arrayMutators }}
        render={({ handleSubmit, form, values, errors, hasValidationErrors }) => {
          mainForm = mainForm || form;
          return (
            <form onSubmit={handleSubmit}>
              <FieldArray name="beneficiaryData">
                {({ fields }) => (
                  <>
                    {fields.map((name: string, index: number) => (
                      <div key={name} className={styles.bulkRow}>
                        <div className={styles.bulkFields}>
                          <Input
                            name={`${name}.beneficiaryName`}
                            label="Full Name"
                            disabled={submitting}
                            className={styles.formField}
                            error={checkFieldError(errors, `${name}.beneficiaryName`)}
                            helperText={errorText(errors, `${name}.beneficiaryName`)}
                          />
                          <Select
                            name={`${name}.networkCode`}
                            label="Network"
                            disabled={submitting || isLookingUp}
                            error={checkFieldError(errors, `${name}.networkCode`)}
                            helperText={errorText(errors, `${name}.networkCode`)}
                            SelectProps={{
                              onChange: handleChangeNetworkType(form, values, index),
                            }}
                            className={styles.formField}
                          >
                            {networks.map(({ name: networkName, code }) => (
                              <SelectItem key={code} value={code}>
                                {networkName}
                              </SelectItem>
                            ))}
                          </Select>
                          <PhoneInput
                            name={`${name}.beneficiaryWalletNumber`}
                            label="Beneficiary Wallet Number"
                            onBlur={handleChangeWalletNumber(values, index, form)}
                            disabled={submitting || isLookingUp}
                            className={styles.formField}
                            error={checkWallenNameFieldError(errors, `${name}.beneficiaryWalletNumber`)}
                            helperText={errorWalletNameText(errors, `${name}.beneficiaryWalletNumber`)}
                          />
                          <Input
                            name={`${name}.walletName`}
                            label="Wallet Name"
                            disabled
                            className={styles.formField}
                            error={checkFieldError(errors, `${name}.walletName`)}
                            helperText={errorText(errors, `${name}.walletName`)}
                          />
                        </div>
                        {loadingRow(index) && <Loader size={40} />}
                        {!loadingRow(index) && fields.length && fields.length > 1 ? (
                          <>
                            <div
                              className={styles.removeRowBtn}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                fields.remove(index);
                                removeBeneficiaryAtIndex(index);
                              }}
                              onKeyPress={noop}
                            >
                              <img src={trash} alt="delete icon" width="40" height="40" />
                            </div>

                            <Button
                              className={styles.deleteBeneficiaryMobileBtn}
                              onClick={() => {
                                fields.remove(index);
                                removeBeneficiaryAtIndex(index);
                              }}
                            >
                              Delete Beneficiary
                            </Button>
                          </>
                        ) : null}
                      </div>
                    ))}
                    <button type="button" className={styles.addBeneficiaryBtn} onClick={handleAddBen}>
                      <img src={plus} alt="add icon" width="12" height="12" />
                      Add one more beneficiary
                    </button>
                    <Button
                      type="submit"
                      className={styles.submitBtn}
                      disabled={hasValidationErrors || !form.getState().dirty || isLookingUp || submitting}
                    >
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                  </>
                )}
              </FieldArray>
              <WarningModal
                open={showWarning}
                submitName="Delete"
                submitting={warningSubmitting}
                onSubmit={onSubmitWarning}
                onClose={closeWarning}
              >
                Are you sure you want to delete the Bulk Beneficiary List?
              </WarningModal>
              <StatusModal
                open={showStatus}
                status={successMessage ? 'success' : 'failure'}
                onRetry={onCloseStatusModal}
                onClose={onCloseStatusModal}
                onRetryText="Continue"
              >
                {successMessage || errorMessage}
              </StatusModal>
            </form>
          );
        }}
      />
    </BulksWrapper>
  );
};

export default EditMobileBulkBen;
